import { useAtom } from "jotai";
import { homePinsAtom } from "./atoms";
import { getPinIcon } from "../../../utils/maps";
import { Venue } from "../../../types/venue";
import { useMemo } from "react";

export function useHomeVenues() {
  const [{ data, error, isLoading, isFetching }] = useAtom(homePinsAtom);

  // Memoize the locations array
  const locations = useMemo(() => {
    // If there's no data or no locations, return an empty array
    if (!data?.locations) {
      return undefined;
    }

    // The search API has occasionally returned the same venue id twice, which
    // crashes the map (duplicate marker keys). Dedupe defensively.
    const seenIds = new Set<string>();

    // This .map() will now only run if `data.locations` changes
    return data.locations
      .filter((e: Venue) => {
        if (seenIds.has(e.id)) return false;
        seenIds.add(e.id);
        return true;
      })
      .map((e: Venue) => {
        if (e.venuePinType) return e;
        return {
          ...e,
          venuePinType: getPinIcon(e),
        };
      });
  }, [data?.locations]); // <--- Dependency array for useMemo

  // Rendered outside the list, but needs the same pin type.
  const focus = useMemo(() => {
    if (!data?.focus?.items?.length) {
      return undefined;
    }
    return {
      ...data.focus,
      items: data.focus.items.map((item) => ({
        ...item,
        venue: item.venue.venuePinType
          ? item.venue
          : { ...item.venue, venuePinType: getPinIcon(item.venue) },
      })),
    };
  }, [data?.focus]);

  return {
    locations,
    focus,
    newRegion: data?.newRegion,
    error,
    isLoading,
    isFetching,
    newLocation: data?.newLocation,
    search_original_distance: data?.search_original_distance,
    search_distance_used: data?.search_distance_used,
    total_count: data?.total_count,
    filtered_venues: data?.filtered_venues,
    empty_reason: data?.empty_reason,
    data,
  };
}